import { Box, Slide, Stack } from '@mui/material';
import { LinkReturnToMain } from 'Components';
import { SearchFieldTrigger } from 'Components/SearchFieldTrigger';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { RefObject } from 'react';
import { sendEvent } from 'Utils/sendEvent';
import { SearchInput } from '../SearchInput';
import { SearchContent } from '../SearchContent';
import { ContentPaper } from '../ContentPaper';
import { IRelatedAction, IResult } from '../hooks/useSearchLogic';

interface SearchCurtainProps {
  isActive: boolean;
  value: string;
  results: IResult[];
  relatedActions: IRelatedAction[];
  isLoading: boolean;
  showNoResults: boolean;
  showResults: boolean;
  hasContent: boolean;
  minSearchLength: number;
  placeholder?: string;
  source?: string;
  inputRef: RefObject<HTMLInputElement | null>;
  searchBarRef?: RefObject<HTMLButtonElement | null>;
  onActivate?: () => void;
  onClose: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onClear: () => void;
  onActivityClick: (action: IRelatedAction) => void;
}

export const SearchCurtain = ({
  isActive,
  value,
  results,
  relatedActions,
  isLoading,
  showNoResults,
  showResults,
  hasContent,
  minSearchLength,
  placeholder,
  source,
  inputRef,
  searchBarRef,
  onActivate,
  onClose,
  onInputChange,
  onKeyDown,
  onClear,
  onActivityClick,
}: SearchCurtainProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);
  const isDesktop = useIsDesktop();
  const headerHeight = isDesktop ? 80 : 72;
  const isFromHeader = source === 'header';

  return (
    <>
      {!isFromHeader && !isActive && (
        <SearchFieldTrigger
          triggerRef={searchBarRef}
          label={dictionary.searchInfoOrActions}
          onActivate={() => {
            if (source === 'home') {
              sendEvent('Search Page', 'HomePageSearch');
            } else if (source === 'activities') {
              sendEvent('Activities Page', 'ActionsLobbySearch');
            }
            onActivate?.();
          }}
          height='56px'
          borderRadius='30px'
          sx={{
            width: '100%',
            mt: '24px',
            '&:focus': { outlineColor: 'rgb(21, 82, 232)' },
          }}
          typographySx={{
            fontFamily: 'Assistant',
            fontSize: '18px',
            color: '#344266',
            fontWeight: 400,
          }}
        />
      )}

      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: `${headerHeight}px`,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1298,
          bgcolor: 'transparent',
          pointerEvents: isActive ? 'auto' : 'none',
          visibility: isActive ? 'visible' : 'hidden',
        }}
      />
      <Slide
        direction='down'
        in={isActive}
        timeout={isDesktop ? { enter: 350, exit: 300 } : 0}
        easing={
          isDesktop
            ? {
                enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
                exit: 'cubic-bezier(0.4, 0, 0.6, 1)',
              }
            : undefined
        }
      >
        <Box
          sx={{
            position: 'fixed',
            top: `${headerHeight}px`,
            left: 0,
            right: 0,
            bottom: isDesktop ? undefined : 0,
            width: '100%',
            zIndex: 1299,
            background:
              'linear-gradient(180deg, rgba(245, 247, 254, 1) 0%, rgba(231, 237, 255, 1) 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pb: isDesktop ? '80px' : '24px',
            overflow: isDesktop ? undefined : 'auto',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          <Stack
            sx={{
              width: '100%',
              maxWidth: '800px',
              px: '24px',
              pt: isDesktop ? '40px' : '24px',
            }}
          >
            {!isDesktop && (
              <Box sx={{ mb: '24px' }}>
                <LinkReturnToMain
                  label={dictionary.back}
                  onClick={onClose}
                  tabIndex={0}
                />
              </Box>
            )}
            <Box
              sx={{ width: isDesktop ? '564px' : '100%', alignSelf: 'center' }}
            >
              <SearchInput
                value={value}
                inputRef={inputRef}
                useCurtain={true}
                source={source}
                onInputChange={onInputChange}
                onKeyDown={onKeyDown}
                onClear={onClear}
              />
            </Box>
          </Stack>
          <Box
            sx={{
              mt: '24px',
              px: '24px',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {(hasContent || value.length > 0) &&
              (isDesktop ? (
                <ContentPaper>
                  <SearchContent
                    value={value}
                    results={results}
                    relatedActions={relatedActions}
                    isLoading={isLoading}
                    showNoResults={showNoResults}
                    showResults={showResults}
                    minSearchLength={minSearchLength}
                    onActivityClick={onActivityClick}
                  />
                </ContentPaper>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <SearchContent
                    value={value}
                    results={results}
                    relatedActions={relatedActions}
                    isLoading={isLoading}
                    showNoResults={showNoResults}
                    showResults={showResults}
                    minSearchLength={minSearchLength}
                    onActivityClick={onActivityClick}
                  />
                </Box>
              ))}
          </Box>
        </Box>
      </Slide>
    </>
  );
};
